// /teacher/compliance — calm, read-only "my certifications + renewals".
//
// Shows the staff member's own staff_certifications rows grouped by status
// with attached proof access where applicable. No add/edit — admin owns
// records; teachers see what they have.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  STATUS_META,
  daysUntilExpiry,
  deriveStatus,
  getCertDocumentUrl,
  listCertificationsForProfile,
} from "@/lib/compliance";

export default function TeacherCompliance() {
  const { profile } = useAuth();
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) return;
    setLoading(true);
    listCertificationsForProfile(profile.id)
      .then((rows) => setCerts(rows || []))
      .catch((err) => toast.error(err.message || "Could not load certifications"))
      .finally(() => setLoading(false));
  }, [profile?.id]);

  const grouped = useMemo(() => {
    const buckets = { expired: [], expiring_soon: [], valid: [], revoked: [] };
    for (const c of certs) buckets[deriveStatus(c)].push(c);
    return buckets;
  }, [certs]);

  const total = certs.length;
  const attentionCount = grouped.expired.length + grouped.expiring_soon.length;

  return (
    <div data-testid="teacher-compliance" className="space-y-5">
      <div>
        <Link
          to="/teacher"
          data-testid="teacher-compliance-back"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} weight="bold" /> Back to Home
        </Link>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
          My certifications
        </h1>
        <p className="mt-2 max-w-xl text-neutral-600">
          Renewal reminders are sent automatically at 30, 14, 7 and 1 day
          before each expiry, plus on the day itself. Reach out to your
          director if anything here looks wrong.
        </p>
      </div>

      <section className="grid grid-cols-2 gap-3">
        <Stat testId="teacher-compliance-stat-total" label="On file" value={total} />
        <Stat testId="teacher-compliance-stat-attention" label="Needs attention" value={attentionCount} tone={attentionCount > 0 ? "warn" : "ok"} />
      </section>

      {loading && <p className="text-sm text-neutral-500">Loading…</p>}

      {!loading && total === 0 && (
        <p
          data-testid="teacher-compliance-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500"
        >
          Your director hasn't recorded any certifications for you yet.
        </p>
      )}

      {grouped.expired.length > 0 && (
        <Group label="Expired" tone="alert" rows={grouped.expired} testId="teacher-compliance-group-expired" />
      )}
      {grouped.expiring_soon.length > 0 && (
        <Group label="Expiring soon" tone="warn" rows={grouped.expiring_soon} testId="teacher-compliance-group-expiring" />
      )}
      {grouped.valid.length > 0 && (
        <Group label="Current" tone="ok" rows={grouped.valid} testId="teacher-compliance-group-valid" />
      )}
      {grouped.revoked.length > 0 && (
        <Group label="Revoked" tone="muted" rows={grouped.revoked} testId="teacher-compliance-group-revoked" />
      )}
    </div>
  );
}

function Stat({ testId, label, value, tone }) {
  const toneClass = {
    ok:    "border-emerald-200",
    warn:  "border-amber-300",
  }[tone || ""] || "border-neutral-200";
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-4 ${toneClass}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function Group({ label, tone, rows, testId }) {
  return (
    <section data-testid={testId}>
      <p className="mb-2 text-sm font-bold uppercase tracking-[0.15em] text-neutral-500">{label}</p>
      <ul className="space-y-2">
        {rows.map((c) => (
          <CertCard key={c.id} cert={c} />
        ))}
      </ul>
    </section>
  );
}

function CertCard({ cert }) {
  const status = deriveStatus(cert);
  const meta = STATUS_META[status];
  const days = daysUntilExpiry(cert.expires_on);
  const onOpenDoc = async () => {
    const url = await getCertDocumentUrl(cert.document_path);
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("Document not available");
  };
  return (
    <li
      data-testid={`teacher-compliance-cert-${cert.id}`}
      data-status={status}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
          {status === "expired" || status === "expiring_soon" ? (
            <Warning size={16} weight="duotone" />
          ) : (
            <ShieldCheck size={16} weight="duotone" />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">{cert.cert_type?.label || "Certification"}</p>
          <p className="text-[11px] text-neutral-500">
            Issued {cert.issued_on}
            {cert.expires_on
              ? ` · expires ${cert.expires_on}${days !== null ? ` (${days >= 0 ? `in ${days}d` : `${Math.abs(days)}d ago`})` : ""}`
              : " · no expiry"}
          </p>
          {cert.notes && <p className="mt-0.5 text-[11px] italic text-neutral-500">"{cert.notes}"</p>}
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
          {meta.label}
        </span>
      </div>
      {cert.document_path && (
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            data-testid={`teacher-compliance-cert-${cert.id}-doc`}
            onClick={onOpenDoc}
            className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <FileText size={11} weight="duotone" /> Open proof
          </button>
        </div>
      )}
    </li>
  );
}
